'use client';

import React, { Component, ReactNode } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="bg-white rounded-[6px] border-0 p-8 text-center shadow-sm">
          <div className="max-w-md mx-auto">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="ExclamationTriangleIcon" className="w-6 h-6 text-slate-400" />
            </div>
            <h2 className="text-foreground font-heading font-semibold text-sm mb-2">Something went wrong</h2>
            <p className="text-slate-500 font-body text-xs mb-6">
              This tab encountered an error. Please try refreshing the page or switch to another tab.
            </p>
            <button
              type="button"
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-5 py-2.5 rounded-[6px] font-caption font-medium text-xs text-white transition-smooth hover:opacity-90"
              style={{ backgroundColor: '#236434' }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
